import { inject, Injectable, NgZone } from '@angular/core';
import { CompiereDataGridFilterType, DataStoreRequest } from '@compiere-ws/models/compiere-data-json';
import { ProcessPingInfo, ProcessPingInfoPara } from '@compiere-ws/models/process-ping-info';
import { OperatorFilterType } from '@iupics-components/models/universal-filter';
import { DataStoreService } from '@iupics-manager/managers/data-store/data-store.service';
import { SecurityManagerService } from '@iupics-manager/managers/security-manager/security-manager.service';
import { AuditService } from '@iupics-components/specific/window/audit-window-ui/audit.service';
import { firstValueFrom, Observable, Subject, Subscription } from 'rxjs';
import { SocketService } from '../socket/socket.service';

@Injectable({
  providedIn: 'root',
})
export class ProcessInProgressService {
  private openedWindow: string[] = [];
  private timeouts: NodeJS.Timeout[] = [];
  private progressSocketSub: Subscription;
  private pings: ProcessPingInfo[] = [];
  private pings$ = new Subject<ProcessPingInfo[]>();
  readonly #zone = inject(NgZone);
  readonly #auditService = inject(AuditService);

  constructor(
    private socketService: SocketService,
    private store: DataStoreService,
    private smService: SecurityManagerService
  ) {}

  init(): void {
    this.socketService.initSocket();

    this.progressSocketSub?.unsubscribe();
    // received outside Angular: the text messages (most of them for a system admin, every user's processes) are
    // dropped without a check of the whole app; a process ping enters Angular
    this.progressSocketSub = this.socketService.enableProcessInProgressChannel(true).subscribe((data) => {
      if (typeof data === 'string') {
        return;
      }
      this.#zone.run(() => this.#onPing(data));
    });
  }

  #onPing(data: any): void {
    try {
      const ping = new ProcessPingInfo(data);
      // by instance: runs without channel all have Channel null
      const pingIDX = this.pings.findIndex((p) => p.AD_PInstance_ID?.id === ping.AD_PInstance_ID?.id);
      if (pingIDX === -1) {
        this.pings.push(ping);
      } else {
        if (this.pings[pingIDX].Status !== ping.Status) {
          this.pings[pingIDX].Status = ping.Status;
        }
        if (
          this.pings[pingIDX].AD_Org_ID !== undefined &&
          this.pings[pingIDX].AD_Org_ID !== null &&
          this.pings[pingIDX].AD_User_ID !== undefined &&
          this.pings[pingIDX].AD_User_ID !== null &&
          ping.Status !== 'finish' &&
          ping.Status !== 'cancel'
        ) {
          this.pings$.next(this.pings);
        }

        if (ping.Status === 'finish' || ping.Status === 'cancel') {
          this.pings$.next(this.pings);
          if (this.openedWindow.length === 0) {
            this.removeFinishedProcess(this.pings[pingIDX]);
          }
        }
      }
    } catch (e) {
      console.error(e);
    }
  }

  updateSocket(): void {
    this.socketService.disableProcessInProgressChannel();
    this.init();
  }

  getProcessInfo(ping: ProcessPingInfo): void {
    const language = this.smService.getIupicsUserContext()['#AD_Language'];
    const pinstance = ping.AD_PInstance_ID;
    const datastoreRequest: DataStoreRequest = {
      windowId: null,
      parent_constraint: null,
      compiereRequest: {
        tableName: 'INSTANCE_PARA_PING_V',
        startRow: 0,
        filterModel: {
          AD_PInstance_ID: {
            filterType: CompiereDataGridFilterType.NUMBER,
            operators: [OperatorFilterType.EQUALS],
            values: [pinstance.id],
          },
          AD_Language: {
            filterType: CompiereDataGridFilterType.TEXT,
            operators: [OperatorFilterType.EQUALS],
            values: [language],
          },
        },
      },
    };

    const sub = this.store.getDataGrid(datastoreRequest, true).subscribe(({ data }) => {
      const pIDX = this.pings.findIndex(
        (p) => p.AD_PInstance_ID.id === pinstance.id && p.AD_Process_ID.id === ping.AD_Process_ID.id
      );
      this.pings[pIDX].setFromProcessPingInfoPara(data as ProcessPingInfoPara[]);
      sub.unsubscribe();
    });
  }

  /*
   * Retourne un Observable avec la référence du tableau de pings.
   * La référence peut être modifiée car le subscribe qui écoute sera trigger à chaque changement.
   */
  watchProcessInProgress(): Observable<ProcessPingInfo[]> {
    this.init();
    return this.pings$.asObservable();
  }

  private removeFinishedProcess(ping: ProcessPingInfo): void {
    const timeout = setTimeout(() => {
      const idx = this.pings.indexOf(ping);
      this.pings.splice(idx, 1);
      this.pings$.next(this.pings);
      this.timeouts = this.timeouts.filter((t) => t !== timeout);
    }, 5000);
    this.timeouts.push(timeout);
  }

  openWindow(activeTabID: any): void {
    this.openedWindow.push(activeTabID);
    for (const timeout of this.timeouts) {
      clearTimeout(timeout);
    }
  }

  closeWindow(activeTabID: any): void {
    this.openedWindow = this.openedWindow.filter((id) => id !== activeTabID);
    if (this.openedWindow.length === 0) {
      for (const ping of this.pings) {
        if (ping.Status === 'finish' || ping.Status === 'cancel') {
          this.removeFinishedProcess(ping);
        }
      }
    }
  }
  // Method to send stop signal via WebSocket
  /**
   * true once the stop signal is sent. A run without channel (scheduler, API, background) is stopped by the server
   * from its AD_PInstance_ID (the socket ignores a null channel).
   */
  async stopProcess(channel: string, instanceId?: number): Promise<boolean> {
    try {
      if ((!channel || channel === 'null') && instanceId > 0) {
        const answer = await firstValueFrom(this.#auditService.stopExecution(instanceId));
        return answer?.result === 'STOPPED' || answer?.result === 'SIGNALLED';
      }
      await this.socketService.stopProcess(channel); // Send the stop signal
      return true;
    } catch (err) {
      console.error('Failed to send stop request', err);
      return false;
    }
  }
}
